import * as React from "react";
import get from "lodash/get";

import "./TextArea.css";
import {
  isEditMode,
  onClickSendToMerchant,
} from "../../utils/sendClickToMerchant";
import * as actions from "../../actions";
import { interpolate } from "../../utils/string";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { flattenObject } from "../../utils/flattenObject";

export interface TextAreaProps extends ComponentBaseProps {
  // tslint:disable-next-line:no-any
  cols: number;
  classNames: string;
  clearWidgetFormError: typeof actions.clearWidgetFormError;
  data: any;
  errorMessage?: string;
  onBlur: () => void;
  shouldFocus?: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
  rows: number;
}

export class TextArea extends ComponentBase<TextAreaProps> {
  private hasUserChangedMessage = false;
  private previousValue = "";

  handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const formErrors = flattenObject(get(this.props, "formErrors", {}));
    const error = get(formErrors, this.props.name);
    const newValue = event.target.value;
    const defaultValue = this.props.defaultValue || "";
    const currentValue = get(this.props.data, this.props.name, "");

    // Track if user has manually changed the message (value differs from default)
    if (newValue !== defaultValue && newValue !== currentValue) {
      this.hasUserChangedMessage = true;
    }

    // If message is cleared, reset to default (unless we're already at default)
    if (newValue === "" && defaultValue) {
      this.hasUserChangedMessage = false;
      this.onFormChange(defaultValue);

      return;
    }

    // If message matches default after being changed, reset the flag
    if (newValue === defaultValue && this.hasUserChangedMessage) {
      this.hasUserChangedMessage = false;
    }

    this.previousValue = newValue;
    this.onFormChange(this.previousValue);
    const formErrors2 = flattenObject(get(this.props, "formErrors", {}));
    const error2 = get(formErrors2, this.props.name);

    // if the error message has changed we want to remove the error bar. this gives the user a chance to submit
    if (error !== error2) {
      this.props.clearWidgetFormError(this.props.name);
    }
  };

  onBlur = () => {
    if (isEditMode()) {
      return;
    }

    const formErrors = flattenObject(get(this.props, "formErrors", {}));

    if (formErrors[this.props.name]) {
      this.validateField(true);
    }
  };

  render() {
    const {
      classNames,
      style,
      placeholder,
      name,
      configName,
      data,
      rows,
      cols,
      isEditing,
    } = this.props;

    // if we're not in edit mode we'll replace {{ }} with a blank string
    const defaultToBlank = !isEditMode();
    const value = interpolate(get(data, name, ""), data, defaultToBlank);
    const formErrors = flattenObject(get(this.props, "formErrors", {}));
    const hasError = formErrors[this.props.name];

    return (
      <textarea
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        placeholder={placeholder}
        value={value}
        style={style}
        className={`TextArea-input ${classNames} ${
          hasError ? "input-error" : ""
        } ${!isEditing ? "TextArea-input-preview" : ""}`}
        onChange={this.handleOnChange}
        onBlur={this.onBlur}
        onClick={onClickSendToMerchant(configName)}
        rows={rows}
        cols={cols}
        aria-label={get(this.props, "label", "")}
        tabIndex={0}
        ref={(component) => (this.component = component)}
      />
    );
  }
}
